import React, { useCallback, useEffect, useRef, useState } from 'react';

import { di } from 'react-magnetic-di';

import { Stack } from '@atlaskit/primitives/compiled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { JsmSelectHardwareTypesPayload } from '@atlassian/conversation-assistant-message-actions-api/jsm';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { SelectHardwareTypesCard } from './SelectHardwareTypesCard';

type JsmSelectHardwareTypesActionProps = ActionRendererFor<
	typeof ActionKeys.JsmSelectHardwareTypes | typeof ActionKeys.JsmSelectHardwareTypesSkill
>;

const ACTION_SUBJECT = 'jsmSelectHardwareTypesCard';

export const JsmSelectHardwareTypesAction = ({
	actions,
	getStatus,
	sendMessageConfirmedTools,
	conversationId,
	messageId,
	recipientAgentId,
	isHistoricalMessage,
}: JsmSelectHardwareTypesActionProps): React.JSX.Element | null => {
	di(useAnalytics);

	const { sendAnalyticsEvent } = useAnalytics();
	const [awaitingInvocationId, setAwaitingInvocationId] = useState<string | null>(null);
	const [succeededInvocationIds, setSucceededInvocationIds] = useState<Set<string>>(new Set());
	const viewedInvocationIdsRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (isHistoricalMessage || actions.length === 0) {
			return;
		}

		actions.forEach((action) => {
			if (viewedInvocationIdsRef.current.has(action.invocationId)) {
				return;
			}

			viewedInvocationIdsRef.current.add(action.invocationId);
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: ACTION_SUBJECT,
				actionSubjectId: ACTION_SUBJECT,
				attributes: {
					conversationId,
					messageId,
				},
			});
		});
	}, [actions, conversationId, isHistoricalMessage, messageId, sendAnalyticsEvent]);

	const sendCardInteractionEvent = useCallback(
		(
			actionSubjectId: string,
			attributes?: Record<string, string | number | boolean | undefined>,
		) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: ACTION_SUBJECT,
				actionSubjectId,
				attributes: {
					conversationId,
					messageId,
					...attributes,
				},
			});
		},
		[conversationId, messageId, sendAnalyticsEvent],
	);

	const handleExpandToggle = useCallback(
		(isExpanded: boolean) => {
			sendCardInteractionEvent(`${ACTION_SUBJECT}ExpandToggle`, { isExpanded });
		},
		[sendCardInteractionEvent],
	);

	const handleHardwareTypeToggle = useCallback(
		(hardwareTypeName: string, isSelected: boolean) => {
			sendCardInteractionEvent(`${ACTION_SUBJECT}HardwareTypeCheckbox`, {
				hardwareTypeName,
				isSelected,
			});
		},
		[sendCardInteractionEvent],
	);

	const handleNextForAction = useCallback(
		async (action: (typeof actions)[number], selectedHardwareTypes: string[]) => {
			sendCardInteractionEvent(`${ACTION_SUBJECT}NextButton`);

			if (!sendMessageConfirmedTools || !conversationId || !messageId) {
				return;
			}

			setAwaitingInvocationId(action.invocationId);

			try {
				await sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: {
						interactionSource: 'jsmSelectHardwareTypesAction',
					},
					confirmed_tools: {
						message_id: messageId,
						tools: [
							{
								key: action.key,
								tool_id: action.actionId,
								tool_source: action.toolSource,
								invocation_id: action.invocationId,
								data: {
									human_message_id: action.humanMessageId ?? '',
									arguments: {
										inputs: {
											projectId: action.data.projectId,
											userInput: `I want to track ${selectedHardwareTypes.join(', ')}`,
										},
									},
								},
							},
						],
					},
				});

				setSucceededInvocationIds((prev) => new Set(prev).add(action.invocationId));
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'succeeded',
					actionSubject: ACTION_SUBJECT,
					actionSubjectId: `${ACTION_SUBJECT}ConfirmRequest`,
					attributes: {
						conversationId,
						messageId,
						outcome: 'success',
					},
				});
			} catch {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'failed',
					actionSubject: ACTION_SUBJECT,
					actionSubjectId: `${ACTION_SUBJECT}ConfirmRequest`,
					attributes: {
						conversationId,
						messageId,
						outcome: 'error',
					},
				});
			} finally {
				setAwaitingInvocationId(null);
			}
		},
		[
			sendCardInteractionEvent,
			sendAnalyticsEvent,
			sendMessageConfirmedTools,
			conversationId,
			messageId,
			recipientAgentId,
		],
	);

	if (!actions.length) {
		return null;
	}

	const anyConfirmInFlight = awaitingInvocationId !== null;

	return (
		<Stack space="space.200">
			{actions.map((action) => {
				const actionFinishedPersisted =
					getStatus(action) === 'finished' || action.status === 'finished';
				const isSuccess =
					succeededInvocationIds.has(action.invocationId) || actionFinishedPersisted;

				return (
					<SelectHardwareTypesCard
						key={action.invocationId}
						payload={action.data as JsmSelectHardwareTypesPayload}
						onNext={(selectedHardwareTypes) => handleNextForAction(action, selectedHardwareTypes)}
						onExpandToggle={handleExpandToggle}
						onHardwareTypeToggle={handleHardwareTypeToggle}
						isNextLoading={awaitingInvocationId === action.invocationId}
						isNextDisabled={anyConfirmInFlight || isSuccess}
						isSuccess={isSuccess}
					/>
				);
			})}
		</Stack>
	);
};
